import React, { useEffect, useCallback, useState } from 'react';
import { CachedImage } from './CachedImage';
import { getPreferredImageSrc, peekLightboxItemUrl } from '../../utils/imageSrcCache';
import './lightbox.css';

// Reusable lightbox for image viewing with keyboard navigation
// Props:
// - items: Array<{ id?: string|number, url?: string, thumbnailUrl?: string, caption?: string, getUrl?: () => Promise<string> | string }>
// - initialIndex: number
// - onClose: () => void
// - rightPanel: optional React node rendered beside image (e.g., comments/reactions)
export default function Lightbox({ items, initialIndex = 0, onClose, rightPanel = null }) {
  const [index, setIndex] = useState(initialIndex);
  const item = items && items[index];
  const [imgUrl, setImgUrl] = useState(() => peekLightboxItemUrl(items?.[initialIndex]));
  const [loading, setLoading] = useState(() => !peekLightboxItemUrl(items?.[initialIndex]));
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  const loadImage = useCallback(async () => {
    if (!item) return;
    const immediate = peekLightboxItemUrl(item);
    if (immediate) {
      setImgUrl(immediate);
      setLoading(false);
      setError('');
    } else {
      setLoading(true);
      setError('');
    }
    try {
      const res = typeof item.getUrl === 'function' ? await item.getUrl() : (item.url || '');
      const preferred = getPreferredImageSrc(res || immediate);
      setImgUrl(preferred || res || immediate || '');
    } catch (e) {
      if (!immediate) {
        setError('Failed to load image');
        setImgUrl('');
      }
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // Preload next/prev with the remembered URL when we already have one
  useEffect(() => {
    const preload = (i) => {
      if (!items || !items[i]) return;
      const peeked = peekLightboxItemUrl(items[i]);
      if (peeked) {
        const im = new window.Image();
        im.src = peeked;
        return;
      }
      try {
        const p = typeof items[i].getUrl === 'function' ? items[i].getUrl() : items[i].url;
        Promise.resolve(p).then((url) => {
          const preferred = getPreferredImageSrc(url);
          if (preferred) {
            const im = new window.Image();
            im.src = preferred;
          }
        });
      } catch (_) {}
    };
    preload(index + 1);
    preload(index - 1);
  }, [index, items]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose && onClose();
    } else if (e.key === 'ArrowRight') {
      setIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowLeft') {
      setIndex((i) => Math.max(i - 1, 0));
    }
  }, [items, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Responsive layout: stack panel below image on mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div
      className="y-lightbox"
      data-testid="y-lightbox"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Photo viewer"
    >
      <div
        className="y-lightbox__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`y-lightbox__body ${isMobile ? 'y-lightbox__body--stack' : 'y-lightbox__body--row'}`}
        >
          <div className="y-lightbox__image-wrap">
            {loading && !imgUrl && (
              <div style={{ opacity: 0.8 }}>Loading…</div>
            )}
            {!imgUrl && error && (
              <div>{error}</div>
            )}
            {imgUrl ? (
              <CachedImage
                className="y-lightbox__image"
                src={imgUrl}
                alt={item?.caption || 'Photo'}
                onClick={() => setIndex((i) => Math.min(i + 1, items.length - 1))}
              />
            ) : null}
          </div>
          {rightPanel ? (
            <div className="y-lightbox__panel">
              {rightPanel}
            </div>
          ) : null}
        </div>

        <div className="y-lightbox__chrome" data-testid="y-lightbox-chrome">
          <div className="y-lightbox__caption">
            {item?.caption || ''}
            {items.length > 1 ? (
              <span className="y-lightbox__count">
                {index + 1}
                {' / '}
                {items.length}
              </span>
            ) : null}
          </div>
          <div className="y-lightbox__actions">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={index === 0}
            >
              ‹ Prev
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setIndex((i) => Math.min(i + 1, items.length - 1))}
              disabled={index === items.length - 1}
            >
              Next ›
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
              Close (Esc)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
